/**
 * keyd-map utilities
 *
 * Pure functions extracted from keyd-map.vue + transformation layer
 * for normalizing the SoloLand locationTree response format into
 * the standard keyd-map node shape { l, d, k, pf }.
 *
 * ── Normalized (keyd-map) node shape ─────────────────────────────────────
 *   { l: string, d: Node[], k: { label: string, keyd: string }, pf: number|null }
 *
 * ── SoloLand /locationTree API response ──────────────────────────────────
 *   Envelope: { data: Country[] }
 *
 *   Country (depth 0):
 *     { ext_id: "PLID/128335",
 *       country_iso_code: "TZ",          ← only on countries (optional, e.g. Fiji has none)
 *       name: "Tanzania",                ← clean label, no " , parent" suffix
 *       coordinates: { type: "Point", coordinates: ["lng", "lat"] },  ← GeoJSON, string values
 *       children: Region[] }
 *
 *   Region / District / City (depth 1+):
 *     { ext_id: "PLID/136098",
 *       name: "Regione di Zanzibar Centro-Sud , Tanzania",  ← " , parent" suffix
 *       coordinates: { type: "Point", coordinates: ["lng", "lat"] },
 *       children: Node[] }
 *
 *   69 countries, 1735 total nodes, max depth 6.
 *   All ext_ids use the "PLID/xxxxx" format.
 *
 * ── Alpitour (legacy) node shape ─────────────────────────────────────────
 *   Envelope: { keydsMap: Node[] }
 *   Already in the { l, d, k, pf } shape — passthrough.
 */

// ---------------------------------------------------------------------------
//  Component utilities (extracted from keyd-map.vue methods)
// ---------------------------------------------------------------------------

/**
 * Build a collapse/toggle id from a label string.
 * Strips spaces so bootstrap-vue v-b-toggle can match.
 */
export const get_toggle_id = (str) => str.replaceAll(" ", "");

/**
 * Remove the .active class from every label matching `selector`.
 * Used by get_content, set_position and close_panel.
 */
export const clear_active_labels = (selector) => {
  const active = document.querySelectorAll(selector);
  if (active.length) active[0].classList.remove("active");
};

/**
 * Return the maximum depth of a keyd-map tree.
 * A single node with no children has depth 1.
 *
 * Accepts either a single node or an array of root nodes.
 *
 *   keydmap_max_depth(keydsmap)   // top-level array → returns overall max depth
 *   keydmap_max_depth(node)       // single node
 */
export const keydmap_max_depth = (nodeOrArray) => {
  if (Array.isArray(nodeOrArray)) {
    return nodeOrArray.reduce((max, node) => Math.max(max, keydmap_max_depth(node)), 0);
  }
  if (!nodeOrArray || !nodeOrArray.d || !nodeOrArray.d.length) return 1;
  return 1 + keydmap_max_depth(nodeOrArray.d);
};

// ---------------------------------------------------------------------------
//  SoloLand → keyd-map transformation
// ---------------------------------------------------------------------------

/**
 * Country name (Italian) → continent mapping.
 * Used to group first-level SoloLand nodes (countries) under continent wrappers,
 * matching the old Alpitour keydsMap hierarchy: Continent → Country → Region → City.
 */
const COUNTRY_CONTINENT = {
  // Europa
  "Grecia": "Europa", "Italia": "Europa", "Spagna": "Europa", "Regno Unito": "Europa",
  "Islanda": "Europa", "Polonia": "Europa", "Cipro": "Europa", "Paesi Bassi": "Europa",
  "Norvegia": "Europa", "Albania": "Europa", "Romania": "Europa", "Malta": "Europa",
  "Irlanda": "Europa", "Svezia": "Europa", "Finlandia": "Europa", "Monaco": "Europa",
  // Africa
  "Marocco": "Africa", "Capo Verde": "Africa", "Seychelles": "Africa",
  "Sudafrica": "Africa", "Senegal": "Africa", "Madagascar": "Africa", "Egitto": "Africa",
  "Tunisia": "Africa", "Ciad": "Africa", "Tanzania": "Africa", "Namibia": "Africa",
  "Zimbabwe": "Africa", "Mauritius": "Africa", "Algeria": "Africa", "Uganda": "Africa",
  "Kenya": "Africa", "Botswana": "Africa",
  // Asia
  "Oman": "Asia", "Giappone": "Asia", "Laos": "Asia", "Vietnam": "Asia",
  "Qatar": "Asia", "Cina": "Asia", "Uzbekistan": "Asia", "Sri Lanka": "Asia",
  "India": "Asia", "Giordania": "Asia", "Indonesia": "Asia", "Malesia": "Asia",
  "Turchia": "Asia", "Emirati Arabi Uniti": "Asia", "Cambogia": "Asia",
  "Corea del Sud": "Asia", "Arabia Saudita": "Asia", "Thailandia": "Asia",
  "Singapore": "Asia", "Kazakistan": "Asia", "Maldive": "Asia",
  // America
  "Aruba": "America", "Costa Rica": "America", "Peru": "America", "Anguilla": "America",
  "Messico": "America", "Canada": "America", "Cuba": "America",
  "Saint Martin": "America", "Giamaica": "America", "Guadalupa": "America",
  "Stati Uniti": "America", "Bolivia": "America", "Brasile": "America",
  "Cile": "America", "Colombia": "America", "Argentina": "America",
  "Antigua e Barbuda": "America", "Sint Maarten": "America",
  "Repubblica Dominicana": "America", "Bahamas": "America", "Barbados": "America",
  // Oceania
  "Polinesia Francese": "Oceania", "Nuova Zelanda": "Oceania", "Australia": "Oceania",
  "Isole Cook": "Oceania", "Fiji": "Oceania",
};

/**
 * Group an array of SoloLand country nodes under continent wrapper nodes.
 * Unknown countries fall into "Altro".
 *
 * Input:  [{ ext_id, name, children, ... }]  (countries)
 * Output: [{ ext_id, name, children: [countries...] }]  (continents)
 */
const group_countries_by_continent = (countries) => {
  const groups = {};

  for (const country of countries) {
    const continent = COUNTRY_CONTINENT[country.name] || "Altro";
    if (!groups[continent]) {
      groups[continent] = {
        ext_id: `continent_${continent.toLowerCase()}`,
        name: continent,
        coordinates: null,
        children: [],
      };
    }
    groups[continent].children.push(country);
  }

  // Fixed display order
  const order = ["Europa", "Africa", "Asia", "America", "Oceania", "Altro"];
  return order.filter((c) => groups[c]).map((c) => groups[c]);
};

/**
 * Strip the " , parent" suffix from SoloLand node names.
 * e.g. "Isola di Samos , Egeo Settentrionale" → "Isola di Samos"
 */
const sanitize_name = (name) => {
  if (!name) return "";
  const idx = name.indexOf(" , ");
  return idx !== -1 ? name.substring(0, idx) : name;
};

/**
 * Convert a single SoloLand node into the keyd-map { l, d, k, pf } shape.
 * Applied recursively over children.
 *
 * Discarded fields: coordinates, country_iso_code.
 *
 *   { ext_id: "PLID/136098", name: "Zanzibar , Tanzania", ... }
 *   →
 *   { l: "Zanzibar", k: { label: "Zanzibar", keyd: "PLID/136098" }, pf: null, d: [...] }
 *
 * Nodes whose only children are self-references (same ext_id as parent) are treated
 * as leaves (d: null) so the template's `if (sub.d)` check evaluates correctly.
 * Empty arrays are also normalised to null for the same reason.
 */
const normalize_sololand_node = (node) => {
  const raw_children = Array.isArray(node.children) ? node.children : [];
  const meaningful_children = raw_children.filter((child) => child.ext_id !== node.ext_id);

  // Only collapse to a leaf when ALL children are self-references (no real sub-nodes).
  // When real children exist, keep the full list (incl. any self-ref) so nothing is lost.
  const children_to_use = meaningful_children.length > 0 ? raw_children : null;

  const children = children_to_use
    ? children_to_use.map(normalize_sololand_node)
    : null;

  return {
    l: sanitize_name(node.name),
    k: { label: sanitize_name(node.name), keyd: node.ext_id || "" },
    pf: null,
    d: children,
  };
};

/**
 * Normalize the /locationTree response array.
 * Groups first-level country nodes under continent wrappers,
 * then normalizes the entire tree to { l, d, k, pf }.
 *
 * Input:  [{ ext_id, country_iso_code?, name, coordinates, children }]  (69 countries)
 * Output: [{ l, d, k, pf }]  (continents → countries → regions → ... → localities)
 */
export const normalize_sololand_response = (data) => {
  if (!Array.isArray(data)) return [];
  const grouped = group_countries_by_continent(data);
  return grouped.map(normalize_sololand_node);
};

// ---------------------------------------------------------------------------
//  Alpitour passthrough (already in the right shape)
// ---------------------------------------------------------------------------

/**
 * Identity transform – Alpitour nodes are already { l, d, k, pf }.
 */
export const normalize_alpitour_response = (data) => {
  if (!Array.isArray(data)) return [];
  return data;
};

// ---------------------------------------------------------------------------
//  Auto-detect + normalize
// ---------------------------------------------------------------------------

/**
 * Detect whether an array of nodes is SoloLand or Alpitour format
 * and normalize to the keyd-map node shape.
 *
 * Detection: first node has `name` or `ext_id` → SoloLand.
 */
export const normalize_destinations = (data) => {
  if (!Array.isArray(data) || data.length === 0) return [];

  const sample = data[0];
  if (sample.name !== undefined || sample.ext_id !== undefined) {
    return normalize_sololand_response(data);
  }
  return normalize_alpitour_response(data);
};

/**
 * Extract the node array from a raw API response and normalize it.
 *
 * Actual response envelopes (verified against live API):
 *
 *   Alpitour (flight-and-stay):  response.data = { keydsMap: Node[] }
 *   SoloLand (only-stay):        response.data = { data: Country[] }   ← /locationTree
 */
export const extract_and_normalize = (responseData) => {
  if (!responseData) return [];

  // Alpitour envelope — { keydsMap: [...] }
  if (responseData.keydsMap) {
    return normalize_destinations(responseData.keydsMap);
  }

  // SoloLand /locationTree envelope — { data: Country[] }
  if (responseData.data) {
    if (Array.isArray(responseData.data)) {
      return normalize_destinations(responseData.data);
    }
    // Fallback: nested envelope { data: { data: [...] } }
    if (responseData.data.data && Array.isArray(responseData.data.data)) {
      return normalize_destinations(responseData.data.data);
    }
  }

  // Direct array (already unwrapped)
  if (Array.isArray(responseData)) {
    return normalize_destinations(responseData);
  }

  return [];
};
